import { InterfacePafAutocomplete } from '../../components/paf-autocomplete/paf-autocomplete.custom';
import { InterfaceSearchForm } from './paf-search-form.custom';
import {
  formatDateForDatepicker,
  formatDateStringToReadableDate,
} from '../../utils/date-helper';

import {
  AccessibilityEnum,
  GetTripsByOriginAndDestinationRequest,
  NoticeAttributeEnum,
  PlaceResponse,
  PTViaReference,
  TransportModeEnum,
  TripsByOriginAndDestinationRequestBody,
} from '../../api/clients/journey-service';

import { ApiRequestor } from '../../api-helpers/request';
import { InterfaceI18nConfig } from '../paf-timetable/i18n/i18n.interface';

const transportationModesCheckboxesValues = {
  autoreisezug: 'autoreisezug',
  bus: 'bus',
  kabinenbahn: 'kabinenbahn',
  schiff: 'schiff',
  seilbahn: 'seilbahn',
  standseilbahn: 'standseilbahn',
  tram: 'tram',
  zug: 'zug',
};

const connectionModes = {
  bike: 'bike',
  direct: 'direct',
};

export const getLocationNameFromDidok = async (
  didok: string,
  apiRequestor: ApiRequestor,
  lang: string,
): Promise<string> => {
  const location: PlaceResponse = await apiRequestor.places({
    nameMatch: didok,
  }, lang);

  return location.places[0]?.name;
};

export const getDidokFromValue = async (
  value: InterfacePafAutocomplete['item'],
  apiRequestor: ApiRequestor,
  lang: string,
): Promise<InterfacePafAutocomplete['item']> => {

  // we already have an id, so return it
  if (value.id) {
    return {
      id: value.id,
      name: value.name,
    };
  }

  // there is no name, so it does not make sense to make a request.
  // should not happen in practice, since in this case the search button
  // must be disabled
  if (!value.name) {
    return {
      id: '0',
      name: '0',
    };
  }

  // fetch places for given name
  const fetchedPlaces = await apiRequestor.places({
    nameMatch: value.name,
  }, lang);

  if (fetchedPlaces.places && fetchedPlaces.places.length > 0) {
    return {
      id: fetchedPlaces.places[0].id,
      name: fetchedPlaces.places[0].name,
    };
  }

  // in practice, this should never happen. we should always get any places
  // in the response from the api
  return {
    id: '0',
    name: '0',
  };
};

export const getVias = async (
  viaInputValue: InterfacePafAutocomplete['item'],
  apiRequestor: ApiRequestor,
  lang: string,
): Promise<PTViaReference[]> => {
  const vias: PTViaReference[] = [];

  if (viaInputValue.name) {
    const ensureDidokForVia = await getDidokFromValue(viaInputValue, apiRequestor, lang);
    const viaId = ensureDidokForVia.id as string;

    vias.push({
      stopPlaceId: viaId,
    });
  }

  return vias;
};

export const getTransportModes = (checkboxesTransportation: HTMLPafRadioCheckboxGroupElement): Set<TransportModeEnum> => {
  const modes = new Set<TransportModeEnum>();
  const checkboxes = checkboxesTransportation.getElementsByTagName('paf-radio-checkbox');
  const checkedCheckboxes = Array.from(checkboxes)
    .filter((checkbox) => checkbox.checked === true)
    .map((checkbox) => checkbox.value);

  // API search performance: if all checkboxes are checked, we don't define
  // all transport modes, but send an empty set
  if (checkboxes.length === checkedCheckboxes.length) {
    return modes;
  }

  checkedCheckboxes.forEach((checkbox) => {
    if (checkbox === transportationModesCheckboxesValues.zug) {
      modes.add('HIGH_SPEED_TRAIN');
      modes.add('INTERCITY');
      modes.add('INTERREGIO');
      modes.add('REGIO');
      modes.add('URBAN_TRAIN');
      modes.add('SPECIAL_TRAIN');
    } else if (checkbox === transportationModesCheckboxesValues.bus) {
      modes.add('BUS');
    } else if (checkbox === transportationModesCheckboxesValues.schiff) {
      modes.add('SHIP');
    } else if (checkbox === transportationModesCheckboxesValues.standseilbahn) {
      modes.add('CABLEWAY_GONDOLA_CHAIRLIFT_FUNICULAR');
    } else if (checkbox === transportationModesCheckboxesValues.autoreisezug) {
      modes.add('CABLEWAY_GONDOLA_CHAIRLIFT_FUNICULAR');
    } else if (checkbox === transportationModesCheckboxesValues.seilbahn) {
      modes.add('CABLEWAY_GONDOLA_CHAIRLIFT_FUNICULAR');
    } else if (checkbox === transportationModesCheckboxesValues.kabinenbahn) {
      modes.add('CABLEWAY_GONDOLA_CHAIRLIFT_FUNICULAR');
    } else if (checkbox === transportationModesCheckboxesValues.tram) {
      modes.add('TRAMWAY');
    }
  });

  return modes;
};

export const isDirectConnectionChecked = (checkboxesConnections: HTMLPafRadioCheckboxGroupElement): boolean => {
  const checkboxes = checkboxesConnections.getElementsByTagName('paf-radio-checkbox');
  const connectionCheckbox = Array.from(checkboxes)
    .filter((checkbox) => checkbox.value === connectionModes.direct);

  if (!connectionCheckbox || connectionCheckbox.length < 1) {
    return false;
  }

  return connectionCheckbox[0].checked;
};

export const getNoticeAttributes = (checkboxesConnections: HTMLPafRadioCheckboxGroupElement): Set<NoticeAttributeEnum> => {
  const noticeAttributes = new Set<NoticeAttributeEnum>();
  const checkboxes = checkboxesConnections.getElementsByTagName('paf-radio-checkbox');
  const connectionCheckbox = Array.from(checkboxes)
    .filter((checkbox) => checkbox.value === connectionModes.bike);

  if (!connectionCheckbox || connectionCheckbox.length < 1) {
    return noticeAttributes;
  }

  if (connectionCheckbox[0].checked) {
    noticeAttributes.add('BIKE_TRANSPORT');
  }

  return noticeAttributes;
};

export const getAccessibility = (
  radiosAccessibility: HTMLPafRadioCheckboxGroupElement,
  config: InterfaceI18nConfig,
): AccessibilityEnum => {
  const radios = radiosAccessibility.querySelectorAll(`input[name="${config.inputForm.extendedSearch.a11y.name}"]`) as NodeListOf<HTMLPafRadioCheckboxElement>;

  const checkedRadio = Array.from(radios)
    .filter((radio) => radio.checked);

  if (!checkedRadio || checkedRadio.length < 1) {
    return 'NONE';
  }

  if (checkedRadio[0].disabled) {
    return 'NONE';
  }

  return checkedRadio[0].value as AccessibilityEnum;
};

// JOURNEY SUMMARY
interface InterfaceGetJourneySummaryData {
  config: InterfaceI18nConfig,
  forArrival: boolean,
  date: string,
  time: string,
  destination: string,
  origin: string,
  language: string,
  via: string,
  viaDescription: string,
}

export const getJourneySummaryData = (options: InterfaceGetJourneySummaryData): InterfaceSearchForm['summary'] => ({
  assistiveTextDetails: options.forArrival
    ? options.config.results.journeySummary.assistiveTextDetails.arrival[options.language]
    : options.config.results.journeySummary.assistiveTextDetails.departure[options.language],
  assistiveTextTitle: options.config.results.journeySummary.assistiveTextTitle[options.language],
  changeButtonText: options.config.results.journeySummary.changeButtonText[options.language],
  date: formatDateStringToReadableDate(options.date),
  departureArrival: options.forArrival
    ? options.config.results.journeySummary.departureArrival.arrival[options.language]
    : options.config.results.journeySummary.departureArrival.departure[options.language],
  destination: options.destination,
  headingLevel: 2,
  origin: options.origin,
  time: options.time,
  via: options.via,
  viaDescription: options.viaDescription,
});

// used to overwrite i18n with values from url-parameters
export const adjustAppConfigFromUrlParams = (
  request: GetTripsByOriginAndDestinationRequest,
  config: InterfaceI18nConfig,
): InterfaceI18nConfig => {
  if (!request) {
    return config;
  }

  const requestBody = request.tripsByOriginAndDestinationRequestBody;

  config.inputForm.inputFrom.value = requestBody.origin;
  config.inputForm.inputTo.value = requestBody.destination;
  config.inputForm.date.value = formatDateForDatepicker(new Date(requestBody.date));
  config.inputForm.time.value = requestBody.time;
  config.inputForm.toggleArrivalDeparture.value = requestBody.forArrival
    ? 'an'
    : 'ab';

  // via
  if (requestBody.vias?.length > 0) {
    config.inputForm.extendedSearch.via.value = requestBody.vias[0].stopPlaceId;
  }

  // EXTENDED SEARCH

  //  MEANS OF TRANSPORTATION
  config.inputForm.extendedSearch.meansOfTransportation.inputs.forEach((input) => {
    if (input.value === transportationModesCheckboxesValues.bus) {
      input.checked = requestBody.includeTransportModes.has('BUS');
    }

    if (input.value === transportationModesCheckboxesValues.zug) {
      const hsType = requestBody.includeTransportModes.has('HIGH_SPEED_TRAIN');
      const icType = requestBody.includeTransportModes.has('INTERCITY');
      const irType = requestBody.includeTransportModes.has('INTERREGIO');
      const rType = requestBody.includeTransportModes.has('REGIO');
      const utType = requestBody.includeTransportModes.has('URBAN_TRAIN');
      const stType = requestBody.includeTransportModes.has('SPECIAL_TRAIN');

      input.checked = hsType || icType || irType || rType || utType || stType;
    }

    if (input.value === transportationModesCheckboxesValues.schiff) {
      input.checked = requestBody.includeTransportModes.has('SHIP');
    }

    if (input.value === transportationModesCheckboxesValues.standseilbahn) {
      input.checked = requestBody.includeTransportModes.has('CABLEWAY_GONDOLA_CHAIRLIFT_FUNICULAR');
    }

    if (input.value === transportationModesCheckboxesValues.autoreisezug) {
      input.checked = requestBody.includeTransportModes.has('CABLEWAY_GONDOLA_CHAIRLIFT_FUNICULAR');
    }

    if (input.value === transportationModesCheckboxesValues.seilbahn) {
      input.checked = requestBody.includeTransportModes.has('CABLEWAY_GONDOLA_CHAIRLIFT_FUNICULAR');
    }

    if (input.value === transportationModesCheckboxesValues.kabinenbahn) {
      input.checked = requestBody.includeTransportModes.has('CABLEWAY_GONDOLA_CHAIRLIFT_FUNICULAR');
    }

    if (input.value === transportationModesCheckboxesValues.tram) {
      input.checked = requestBody.includeTransportModes.has('TRAMWAY');
    }
  });

  // check all transportation checkboxes if no transportmode is present
  if (requestBody.includeTransportModes.size === 0) {
    config.inputForm.extendedSearch.meansOfTransportation.inputs.forEach((input) => {
      input.checked = true;
    });
  }

  // CONNECTIONS
  config.inputForm.extendedSearch.connections.inputs.forEach((input) => {
    if (input.value === 'direct') {
      input.checked = requestBody.mobilityFilter.maxTransfers === 0;
    }

    if (input.value === 'bike') {
      input.checked = requestBody.includeNoticeAttributes.has('BIKE_TRANSPORT');
    }
  });

  // a11y

  if (requestBody.includeAccessibility !== 'NONE') {
    config.inputForm.extendedSearch.a11y.checked = true;

    config.inputForm.extendedSearch.a11y.inputs.forEach((input) => {
      input.checked = requestBody.includeAccessibility === input.value;
    });
  }

  return config;
};

export const stringifyConvertSetsToArray = (json: TripsByOriginAndDestinationRequestBody | GetTripsByOriginAndDestinationRequest): string => JSON.stringify(
  json,
  (_key, value) => (value instanceof Set
    ? [...value]
    : value),
);

export const parseConvertArraysToSets = (stringToParse: string): GetTripsByOriginAndDestinationRequest => JSON.parse(
  stringToParse,
  (_key, value) => {
    if (_key !== 'includeTransportModes' && _key !== 'includeNoticeAttributes') {
      return value;
    }

    return (value instanceof Array
      ? new Set(value)
      : value);
  },
);
